import {
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  Box,
  HStack,
  VStack,
  Icon,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  IconButton,
  Tooltip,
  TableContainer,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { motion } from "framer-motion";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  DeleteIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  LockIcon,
  InfoIcon,
} from "@chakra-ui/icons";

// Custom components
import Card from "components/card/Card";

import { useRecoilState, useRecoilValue } from "recoil";
import CreateUserDrawer from "./CreateUserDrawer";
import UpdateUserDrawer from "./UpdateUserDrawer";
import { updateUserDrawerState } from "atoms/updateUserDrawerState";
import { createUserDrawerState } from "atoms/createUserDrawerState";
import DeleteModal from "components/modals/DeleteModal";
import { deleteModalState } from "atoms/deleteModalState";
import AccessManagementModal from "components/modals/AccessManagementModal";
import { loggedInUserState } from "atoms/loggedInUserState";
import { accessLevels } from "utils/accessLevels";
import logger from "infra/logger";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyUAdmins } from "utils/accessLevels";
import { onlyAdmins } from "utils/accessLevels";

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

// Styled components
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const EmptyStateIllustration = () => (
  <MotionFlex
    direction="column"
    align="center"
    justify="center"
    py={12}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Icon as={CheckCircleIcon} boxSize={16} color="brand.500" mb={4} />
    <Text fontSize="xl" fontWeight="bold" mb={2}>
      No Users Found
    </Text>
    <Text color="gray.500" textAlign="center" maxW="md">
      There are no users available at the moment. New users will appear here
      once added.
    </Text>
  </MotionFlex>
);

const RoleBadge = ({ role }) => {
  let color, label;

  switch (role) {
    case "admin":
      color = "purple";
      label = "Admin";
      break;
    case "manager":
      color = "blue";
      label = "Manager";
      break;
    case "property_owner":
      color = "green";
      label = "Property Owner";
      break;
    default:
      color = "gray";
      label = role || "N/A";
  }

  return (
    <Badge
      px={3}
      py={1}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
      fontSize="xs"
      fontWeight="medium"
    >
      {label}
    </Badge>
  );
};

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "brand" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

export default function ColumnsTable(props) {
  const { columnsData, tableData, getUsers } = props;
  const themeColor = "#23A979";

  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);
  const loggedInUser = useRecoilValue(loggedInUserState);
  const role = loggedInUser?.role;
  const canEdit =
    accessLevels[role]?.accessLevel <= 2 || role === "urzza_cms_admin";
  const [updateDrawerState, setUpdateDrawerState] = useRecoilState(
    updateUserDrawerState
  );
  const [createDrawerState, setCreateDrawerState] = useRecoilState(
    createUserDrawerState
  );

  const [searchQuery, setSearchQuery] = useState("");

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 8,
      },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") {
          return rows;
        }

        const lowercasedQuery = query.toLowerCase().trim();

        return rows.filter((row) => {
          const firstName = row.original.FirstName?.toLowerCase() || "";
          const lastName = row.original.LastName?.toLowerCase() || "";
          const email = row.original.Email?.toLowerCase() || "";
          const mobile = row.original.MobileNumber?.toLowerCase() || "";
          const role = row.original.Role?.toLowerCase() || "";
          const fullName = `${firstName} ${lastName}`.toLowerCase();

          return (
            firstName.includes(lowercasedQuery) ||
            lastName.includes(lowercasedQuery) ||
            fullName.includes(lowercasedQuery) ||
            email.includes(lowercasedQuery) ||
            mobile.includes(lowercasedQuery) ||
            role.includes(lowercasedQuery)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    state: { pageIndex, pageSize },
    gotoPage,
    setPageSize,
    canPreviousPage,
    canNextPage,
    pageCount,
    nextPage,
    previousPage,
  } = tableInstance;

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const theadBgColor = useColorModeValue("gray.50", "gray.800");

  const [showDeleteModal, setShowDeleteModal] =
    useRecoilState(deleteModalState);
  const [selectedUser, setSelectedUser] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [accessManagementData, setAccessManagementData] = useState(null);

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const updateHandler = () => {
    setUpdateDrawerState(null);
    setCreateDrawerState(null);
    getUsers();
  };

  const handleConfirmDelete = () => {
    setShowDeleteModal(false);
    getUsers();
  };

  const handleCancelDelete = () => {
    setSelectedUser(null);
    setShowDeleteModal(false);
  };

  const editHandler = (id) => {
    setUpdateDrawerState({ isOpen: true, activeId: id });
  };

  const accessManagementHandler = (user) => {
    setAccessManagementData(user);
    onOpen();
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setGlobalFilter(value);
  };

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <GlassMorphismCard>
        {/* Header with search and title */}
        <Flex
          p={6}
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "start", md: "center" }}
          wrap="wrap"
          gap={4}
        >
          {/* Search inputs */}
          <VStack align="start" spacing={1} minW="300px">
            <Text fontSize="xs" fontWeight="medium" color="gray.500">
              Search Users
            </Text>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by name, email, mobile, role..."
                borderRadius="md"
                width="350px"
              />
            </InputGroup>
            <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
              <Icon as={InfoIcon} boxSize={3} mr={1} />
              Type any keyword to search across all fields
            </Text>
          </VStack>

          {/* Title */}
          <HStack spacing={2} justify="flex-end">
            <Text fontSize="xl" fontWeight="bold">
              Users
            </Text>
            <Badge
              colorScheme="brand"
              fontSize="sm"
              py={1}
              px={2}
              borderRadius="md"
            >
              {tableData.length}
            </Badge>
          </HStack>
        </Flex>

        {/* Table */}
        <TableContainer>
          <Table {...getTableProps()} variant="simple" color="gray.500">
            <Thead bg={theadBgColor}>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={index}
                      py={4}
                      px={6}
                      borderColor={borderColor}
                      textTransform="capitalize"
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      cursor="pointer"
                    >
                      <Flex
                        justify="center"
                        align="center"
                        fontSize={{ sm: "10px", lg: "12px" }}
                        color="gray.400"
                      >
                        {column.render("Header")}
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <Icon as={ChevronDownIcon} ml={1} boxSize={4} />
                          ) : (
                            <Icon
                              as={ChevronRightIcon}
                              ml={1}
                              boxSize={4}
                              transform="rotate(-90deg)"
                            />
                          )
                        ) : null}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <MotionTr
                    {...row.getRowProps()}
                    key={index}
                    whileHover={{ backgroundColor: hoverBg }}
                    transition={{ duration: 0.2 }}
                  >
                    {row.cells.map((cell, index) => {
                      let data = "";
                      if (cell.column.Header === "S.No") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {Number(cell?.row?.id) + 1}
                            </Text>
                          </Flex>
                        );
                      } else if (cell?.column?.Header === "First Name") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {cell?.row?.original?.FirstName}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Last Name") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {cell?.row?.original?.LastName}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Phone Number") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {cell?.row?.original?.MobileNumber}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Email") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {cell?.row?.original?.Email}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Role") {
                        data = (
                          <Flex align="center" justify="center">
                            <RoleBadge role={cell?.row?.original?.Role} />
                          </Flex>
                        );
                      } else if (cell?.column?.Header === "Type") {
                        const type = cell?.row?.original?.Type;
                        const displayText = type ? type : "-";

                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {displayText}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Actions") {
                        const loggedInUserRole = loggedInUser?.role;
                        const entryRole = cell?.row?.original?.Role;
                        const entryLevel = accessLevels[entryRole]?.accessLevel;
                        const loggedInLevel =
                          accessLevels[loggedInUserRole]?.accessLevel;
                        const isDisabled =
                          entryLevel <= 0
                            ? loggedInLevel > 0
                              ? true
                              : false
                            : false;
                        const isSelfAccount =
                          loggedInUser?.userId === cell?.row?.original?.Id;
                        const isSpecialAdmin =
                          cell?.row?.original?.Role === "urzza_cms_admin" ||
                          cell?.row?.original?.Role === "tenant_cms_admin";

                        data = (
                          <Flex align="center" justify="center" gap={2}>
                            {canEdit && !isDisabled && (
                              <Tooltip label="Edit User" placement="top">
                                <IconButton
                                  icon={<EditIcon />}
                                  colorScheme="blue"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    editHandler(cell?.row?.original?.Id)
                                  }
                                  borderRadius="full"
                                  aria-label="Edit User"
                                />
                              </Tooltip>
                            )}

                            {cell.row.original.Role === "manager" && (
                              <RoleBasedRender
                                roles={onlyAdmins}
                                fallback={null}
                              >
                                <Tooltip label="Manage Access" placement="top">
                                  <IconButton
                                    icon={<LockIcon />}
                                    colorScheme="green"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      accessManagementHandler(
                                        cell?.row?.original
                                      )
                                    }
                                    borderRadius="full"
                                    aria-label="Manage Access"
                                  />
                                </Tooltip>
                              </RoleBasedRender>
                            )}

                            {!isDisabled &&
                              !isSelfAccount &&
                              !isSpecialAdmin &&
                              cell.row.original.Role !== "property_owner" && (
                                <RoleBasedRender roles={onlyAdmins}>
                                  <Tooltip label="Delete User" placement="top">
                                    <IconButton
                                      icon={<DeleteIcon />}
                                      colorScheme="red"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        handleDeleteClick(cell?.row?.original)
                                      }
                                      borderRadius="full"
                                      aria-label="Delete User"
                                    />
                                  </Tooltip>
                                </RoleBasedRender>
                              )}
                          </Flex>
                        );
                      }
                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          py={4}
                          px={6}
                          borderColor={borderColor}
                          fontSize="sm"
                        >
                          {data}
                        </Td>
                      );
                    })}
                  </MotionTr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex
          p={4}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={4}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              size="sm"
              borderRadius="md"
              w="120px"
            >
              {[5, 8, 10, 20, 30].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              Showing {page.length} of {tableData.length} results
            </Text>
          </HStack>

          <HStack spacing={2}>
            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => gotoPage(0)}
            >
              <ChevronLeftIcon boxSize={4} mr={1} />
              First
            </PaginationButton>

            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => previousPage()}
            >
              <ChevronLeftIcon boxSize={4} />
            </PaginationButton>

            {pageCount <= 5 ? (
              // If we have 5 or fewer pages, show all page buttons
              [...Array(pageCount)].map((_, i) => (
                <PaginationButton
                  key={i}
                  isActive={pageIndex === i}
                  onClick={() => gotoPage(i)}
                >
                  {i + 1}
                </PaginationButton>
              ))
            ) : (
              // If we have more than 5 pages, show a subset
              <>
                {pageIndex > 1 && (
                  <PaginationButton onClick={() => gotoPage(0)}>
                    1
                  </PaginationButton>
                )}

                {pageIndex > 2 && <Text>...</Text>}

                {pageIndex > 0 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                    {pageIndex}
                  </PaginationButton>
                )}

                <PaginationButton isActive onClick={() => gotoPage(pageIndex)}>
                  {pageIndex + 1}
                </PaginationButton>

                {pageIndex < pageCount - 1 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                    {pageIndex + 2}
                  </PaginationButton>
                )}

                {pageIndex < pageCount - 3 && <Text>...</Text>}

                {pageIndex < pageCount - 2 && (
                  <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                    {pageCount}
                  </PaginationButton>
                )}
              </>
            )}

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => nextPage()}
            >
              <ChevronRightIcon boxSize={4} />
            </PaginationButton>

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => gotoPage(pageCount - 1)}
            >
              Last
              <ChevronRightIcon boxSize={4} ml={1} />
            </PaginationButton>
          </HStack>
        </Flex>
      </GlassMorphismCard>

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        data={selectedUser}
        title="user"
      />
      {createDrawerState?.isOpen && <CreateUserDrawer update={updateHandler} />}
      {updateDrawerState?.isOpen && <UpdateUserDrawer update={updateHandler} />}
      <AccessManagementModal
        isOpen={isOpen}
        onClose={onClose}
        data={accessManagementData}
        onOpen={onOpen}
      />
    </MotionBox>
  );
}
